import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import MarketingLayout from "@/marketing/MarketingLayout";
import Home from "@/marketing/Home";
import Pricing from "@/marketing/Pricing";
import InfoPage from "@/marketing/InfoPage";
import Contact from "@/marketing/Contact";
import About from "@/marketing/About";
import Legal from "@/marketing/Legal";

import Login from "@/auth/Login";
import Register from "@/auth/Register";
import ForgotPassword from "@/auth/ForgotPassword";
import ResetPassword from "@/auth/ResetPassword";
import Onboarding from "@/auth/Onboarding";
import ImpersonationEntry from "@/app/ImpersonationEntry";
import InvoicePay from "@/pay/InvoicePay";
import PaymentSuccess from "@/pay/PaymentSuccess";
import Billing from "@/billing/Billing";
import BillingSuccess from "@/billing/BillingSuccess";

import AppShell from "@/app/AppShell";
import Dashboard from "@/app/pages/Dashboard";
import Customers from "@/app/pages/Customers";
import CustomerDetail from "@/app/pages/CustomerDetail";
import Leads from "@/app/pages/Leads";
import Products from "@/app/pages/Products";
import Quotes from "@/app/pages/Quotes";
import Invoices from "@/app/pages/Invoices";
import Jobs from "@/app/pages/Jobs";
import Planner from "@/app/pages/Planner";
import Expenses from "@/app/pages/Expenses";
import Reports from "@/app/pages/Reports";
import Employees from "@/app/pages/Employees";
import Timekeeping from "@/app/pages/Timekeeping";
import Materials from "@/app/pages/Materials";
import Alfie from "@/app/pages/Alfie";
import Services from "@/app/pages/Services";
import WebsiteAddon from "@/app/pages/WebsiteAddon";
import Settings from "@/app/pages/Settings";
import AuditLog from "@/app/pages/AuditLog";
import SuperAdmin from "@/app/pages/SuperAdmin";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-blue-600 animate-spin" />
  </div>
);

function Protected({ children, adminOnly, allowPending }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (user.role === "super_admin") {
    return adminOnly ? children : <Navigate to="/admin" replace />;
  }
  if (adminOnly) return <Navigate to="/app" replace />;
  if (!user.business_id) return <Navigate to="/onboarding" replace />;
  if (!allowPending && user.business?.billing_status === "pending") return <Navigate to="/billing" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<InfoPage page="features" />} />
            <Route path="/alfie" element={<InfoPage page="alfie" />} />
            <Route path="/website-addon" element={<InfoPage page="website" />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Legal page="privacy" />} />
            <Route path="/terms" element={<Legal page="terms" />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/impersonate" element={<ImpersonationEntry />} />
          <Route path="/pay/i/:iid" element={<InvoicePay />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/billing" element={<Protected allowPending><Billing /></Protected>} />
          <Route path="/billing/success" element={<Protected allowPending><BillingSuccess /></Protected>} />

          <Route path="/admin" element={<Protected adminOnly><SuperAdmin /></Protected>} />

          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="leads" element={<Leads />} />
            <Route path="products" element={<Products />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="planner" element={<Planner />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="reports" element={<Reports />} />
            <Route path="employees" element={<Employees />} />
            <Route path="timekeeping" element={<Timekeeping />} />
            <Route path="materials" element={<Materials />} />
            <Route path="alfie" element={<Alfie />} />
            <Route path="services" element={<Services />} />
            <Route path="website" element={<WebsiteAddon />} />
            <Route path="settings" element={<Settings />} />
            <Route path="audit" element={<AuditLog />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
